// Antecedentes estructurados del paciente (ficha 360°), fiel a reliv:
//  - Generales: Familiares + Personales en MODO LECTURA (Imprimir/Editar) con
//    checkboxes de patologías (detalle en textarea), toggle "sin patología
//    aparente", Discapacidad con descripción+porcentaje, HÁBITOS estructurados
//    (fuma/alcohol/drogas/…) y GINECO-OBSTÉTRICOS estructurados (menarquia,
//    gestas, partos, …) — este último también es opción propia del submenú.
//  - Pediátricos: prenatales / perinatales (con fecha de nacimiento corregida y
//    validaciones de recién nacido) / postnatales.
// Guardan vía med_antecedente_guardar (1 fila por paciente+tipo, datos jsonb).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Pencil, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { AutoTextarea, Campo, Switch, fieldCls, medRpc } from './shared';
import { LOCALE } from '@/lib/utils/regional';

export interface AntecedenteRow {
  id: string;
  tipo: 'familiar' | 'personal' | 'pediatrico';
  sin_patologia: boolean;
  datos: Record<string, unknown>;
  updated_at: string;
}
export interface AntecedentesData {
  familiar: AntecedenteRow | null;
  personal: AntecedenteRow | null;
  pediatrico: AntecedenteRow | null;
}

// Orden fila a fila (grid de 3 columnas) según reliv
export const PATOLOGIAS: [string, string][] = [
  ['diabetes', 'Diabetes'], ['hipertension', 'Hipertensión'], ['cancer', 'Cáncer'],
  ['neurologica', 'Enf. Neurológica'], ['cardiologica', 'Enf. Cardiológica'], ['gastrointestinal', 'Enf. Gastrointestinal'],
  ['infecciosas', 'Enf. Infecciosas'], ['tiroidea', 'Enf. Tiroidea'], ['respiratoria', 'Enf. Respiratoria'],
  ['genitourinaria', 'Enf. Genitourinaria'], ['nefrologica', 'Enf. Nefrológica'], ['inmunologica', 'Enf. Inmunológica'],
  ['hematologica', 'Enf. Hematológica'], ['endocrino_metabolica', 'Enf. Endocrino/Metabólica'], ['traumatica', 'Enf. Traumática'],
  ['catastrofica', 'Enf. Catastrófica'], ['mental', 'Enf. Mental'], ['organos_sentidos', 'Enf. Órganos de los Sentidos'],
  ['oftalmologicas', 'Enf. Oftalmológicas'], ['discapacidad', 'Discapacidad'],
];
const SOLO_PERSONAL = new Set(['discapacidad']);

// Hábitos estructurados (solo Personales)
const HABITOS: [string, string][] = [
  ['fuma', 'Fuma'], ['alcohol', 'Toma Alcohol'], ['drogas', 'Drogas'], ['miccion', 'Micción'],
  ['deposicion', 'Deposición'], ['alimentacion', 'Alimentación'], ['sedentarismo', 'Sedentarismo'],
  ['actividad_fisica', 'Actividad Física'], ['sueno', 'Sueño'],
];
// Gineco-obstétricos estructurados (solo Personales)
const GINECO: [string, string][] = [
  ['menarquia', 'Menarquia'], ['inicio_vida_sexual', 'Inicio Vida Sexual'], ['papanicolau', 'Papanicolau'],
  ['planificacion_familiar', 'Planificación Familiar'], ['ets', 'ETS'], ['gestas', 'Gestas'],
  ['abortos', 'Abortos'], ['partos', 'Partos'], ['cesareas', 'Cesáreas'], ['hijos_vivos', 'Hijos Vivos'],
];

const EXTRAS_FAMILIAR: [string, string][] = [['comentarios', 'Comentarios']];
const EXTRAS_PERSONAL: [string, string][] = [
  ['otros', 'Antecedentes Patológicos'], ['medicamentos', 'Medicamentos'],
  ['alergias', 'Alergias y Alertas'], ['alergias_farmacologicas', 'Alergias Farmacológicas'],
  ['cirugias', 'Cirugías'], ['comentarios', 'Comentarios'],
];

const TIPOS_SANGRE = ['A-', 'A+', 'AB-', 'AB+', 'B-', 'B+', 'O-', 'O+'];

const obj = (d: Record<string, unknown>, k: string): Record<string, string> =>
  (d[k] && typeof d[k] === 'object' && !Array.isArray(d[k]) ? (d[k] as Record<string, string>) : {});
const str = (d: Record<string, unknown>, k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
const tieneContenido = (d: Record<string, unknown>): boolean =>
  Object.entries(d).some(([, v]) =>
    typeof v === 'string' ? v.trim().length > 0
      : v && typeof v === 'object' && !Array.isArray(v) ? Object.values(v as Record<string, unknown>).some((x) => typeof x === 'string' && x.trim().length > 0)
        : false);

// Resumen de una fila para la sección "Antecedentes" de la ficha
export function resumenAntecedente(row: AntecedenteRow | null): string | null {
  if (!row) return null;
  if (row.sin_patologia) return 'Sin patología aparente';
  const d = row.datos ?? {};
  if (row.tipo === 'pediatrico') {
    const lleno = ['prenatales', 'perinatales', 'postnatales'].some((s) => {
      const sec = d[s] as Record<string, unknown> | undefined;
      return sec && Object.values(sec).some((v) => (typeof v === 'string' ? v.trim() : v === true));
    });
    return lleno ? 'Registrado (ver detalle)' : null;
  }
  const pat = obj(d, 'patologias');
  const items = PATOLOGIAS.filter(([k]) => k in pat).map(([k, l]) => (pat[k]?.trim() ? `${l} (${pat[k].trim()})` : l));
  if (items.length) return items.join(' · ');
  return tieneContenido(d) ? 'Registrado (ver detalle)' : null;
}

function Cabecera({ titulo, onVolver }: { titulo: string; onVolver: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={onVolver}><ArrowLeft className="w-4 h-4 mr-1" /> Volver a la ficha</Button>
      <h2 className="text-lg font-semibold">{titulo}</h2>
    </div>
  );
}

function Bloque({ titulo, children, innerRef }: { titulo: string; children: React.ReactNode; innerRef?: (el: HTMLElement | null) => void }) {
  return (
    <section ref={innerRef} className="scroll-mt-4">
      <div className="rounded-t-md bg-primary/10 px-4 py-2.5 text-sm font-semibold">{titulo}</div>
      <div className="rounded-b-md border border-t-0 bg-card px-4 py-3 space-y-3">{children}</div>
    </section>
  );
}

// Sección de texto que se expande desde un botón (reliv: "Medicamentos", "Cirugías"…)
function Expandible({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(() => !!value.trim());
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className={`rounded-md border px-2.5 py-1 text-xs hover:bg-muted ${value.trim() ? 'border-primary/50 text-primary font-medium' : 'text-muted-foreground'}`}>
        {label}{value.trim() ? ' •' : ''}
      </button>
      {open && <AutoTextarea className="mt-1" value={value} onChange={(e) => onChange(e.target.value)} />}
    </div>
  );
}

// Impresión de un bloque de antecedentes (Imprimir de reliv)
function imprimirBloque(titulo: string, nombre: string, filas: [string, string][]) {
  const esc = (s: unknown) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const cuerpo = filas.length
    ? `<table><tbody>${filas.map(([l, v]) => `<tr><td class="l">${esc(l)}</td><td>${esc(v)}</td></tr>`).join('')}</tbody></table>`
    : '<p>Sin registros</p>';
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(titulo)}</title>
    <style>*{font-family:Arial,Helvetica,sans-serif}h1{font-size:16px;margin:0 0 2px}.sub{font-size:12px;color:#555;margin:0 0 12px}
    table{border-collapse:collapse;width:100%;font-size:12px}td{border:1px solid #ccc;padding:4px 6px;vertical-align:top;white-space:pre-wrap}
    td.l{width:220px;font-weight:bold;background:#f7f7f7}p{font-size:12px}@media print{@page{margin:12mm}}</style></head><body>
    <h1>${esc(titulo)}</h1><p class="sub">Paciente: <b>${esc(nombre)}</b> · Emitido: ${esc(new Date().toLocaleString(LOCALE))}</p>
    ${cuerpo}<script>window.onload=function(){window.print();}</script></body></html>`;
  const win = window.open('', '_blank');
  if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
  win.document.write(html); win.document.close();
}

// ---------------------------------------------------------------------------
// Generales: Familiares + Personales (lectura ⇄ edición)
// ---------------------------------------------------------------------------
function BloqueGeneral({ pacienteId, nombre, tipo, titulo, inicial, extras, focoGineco }: {
  pacienteId: string; nombre: string; tipo: 'familiar' | 'personal'; titulo: string;
  inicial: AntecedenteRow | null; extras: [string, string][]; focoGineco?: boolean;
}) {
  const qc = useQueryClient();
  const d = (inicial?.datos ?? {}) as Record<string, unknown>;
  const esPersonal = tipo === 'personal';
  const [lectura, setLectura] = useState(() => !!inicial);
  const [sin, setSin] = useState(inicial?.sin_patologia ?? false);
  const [pat, setPat] = useState<Record<string, string>>(() => ({ ...obj(d, 'patologias') }));
  const [hab, setHab] = useState<Record<string, string>>(() => ({ ...obj(d, 'habitos') }));
  const [habOpen, setHabOpen] = useState(() => Object.keys(obj(d, 'habitos')).length > 0);
  const [gin, setGin] = useState<Record<string, string>>(() => ({ ...obj(d, 'gineco') }));
  const [ginOpen, setGinOpen] = useState(() => Object.values(obj(d, 'gineco')).some((v) => v?.trim()) || !!focoGineco);
  const [porcDisc, setPorcDisc] = useState(() => str(d, 'discapacidad_porcentaje'));
  const [txt, setTxt] = useState<Record<string, string>>(() =>
    Object.fromEntries(extras.map(([k]) => [k, str(d, k)])));
  const refGineco = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focoGineco) {
      setLectura(false);
      setTimeout(() => refGineco.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
    }
  }, [focoGineco]);

  const togglePat = (k: string) => setPat((p) => { const n = { ...p }; if (k in n) delete n[k]; else n[k] = ''; return n; });
  const toggleHab = (k: string) => setHab((p) => { const n = { ...p }; if (k in n) delete n[k]; else n[k] = ''; return n; });

  const guardar = useMutation({
    mutationFn: () => {
      const datos: Record<string, unknown> = {
        patologias: pat,
        ...Object.fromEntries(Object.entries(txt).filter(([, v]) => v.trim())),
      };
      if (esPersonal) {
        if (Object.keys(hab).length) datos.habitos = hab;
        const g = Object.fromEntries(Object.entries(gin).filter(([, v]) => v?.trim()));
        if (Object.keys(g).length) datos.gineco = g;
        if ('discapacidad' in pat && porcDisc.trim()) datos.discapacidad_porcentaje = porcDisc.trim();
      }
      return medRpc('med_antecedente_guardar', { _paciente_id: pacienteId, _tipo: tipo, _sin_patologia: sin, _datos: datos });
    },
    onSuccess: () => {
      toast.success('Antecedentes guardados'); setLectura(true);
      void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const imprimir = () => {
    const filas: [string, string][] = [];
    if (sin) filas.push(['Sin patología aparente', 'Sí']);
    PATOLOGIAS.filter(([k]) => k in pat && (esPersonal || !SOLO_PERSONAL.has(k)))
      .forEach(([k, l]) => filas.push([l, pat[k]?.trim() || 'Sí']));
    if (esPersonal && 'discapacidad' in pat && porcDisc.trim()) filas.push(['Discapacidad — Porcentaje', porcDisc]);
    if (esPersonal) {
      HABITOS.filter(([k]) => k in hab).forEach(([k, l]) => filas.push([`Hábitos — ${l}`, hab[k]?.trim() || 'Sí']));
      GINECO.filter(([k]) => gin[k]?.trim()).forEach(([k, l]) => filas.push([`Gineco-Obstétricos — ${l}`, gin[k]]));
    }
    extras.forEach(([k, l]) => { if (txt[k]?.trim()) filas.push([l, txt[k]]); });
    imprimirBloque(titulo, nombre, filas);
  };

  const lista = PATOLOGIAS.filter(([k]) => esPersonal || !SOLO_PERSONAL.has(k));

  // ---- MODO LECTURA (reliv: bloque con Imprimir/Editar) ----
  if (lectura) {
    const marcadas = lista.filter(([k]) => k in pat);
    const habitosMarcados = HABITOS.filter(([k]) => k in hab);
    const ginecoLleno = GINECO.filter(([k]) => gin[k]?.trim());
    const vacio = !sin && marcadas.length === 0 && habitosMarcados.length === 0 && ginecoLleno.length === 0
      && !extras.some(([k]) => txt[k]?.trim());
    return (
      <Bloque titulo={titulo}>
        {sin && <p className="text-sm font-medium">SIN PATOLOGÍA APARENTE</p>}
        {vacio && !sin && <p className="text-sm text-primary/80">No existen antecedentes registrados</p>}
        {marcadas.length > 0 && (
          <table className="w-full text-sm">
            <tbody>
              {marcadas.map(([k, l]) => (
                <tr key={k} className="border-b last:border-0">
                  <td className="py-1.5 pr-3 font-medium whitespace-nowrap align-top">{l}</td>
                  <td className="py-1.5 whitespace-pre-wrap">{pat[k]?.trim() || '—'}{k === 'discapacidad' && porcDisc.trim() ? ` · Porcentaje: ${porcDisc}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {habitosMarcados.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">Hábitos</h4>
            <p className="text-sm whitespace-pre-wrap">{habitosMarcados.map(([k, l]) => `${l}${hab[k]?.trim() ? ` (${hab[k].trim()})` : ''}`).join(' · ')}</p>
          </div>
        )}
        {ginecoLleno.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">Antecedentes Gineco-Obstétricos</h4>
            <table className="w-full text-sm"><tbody>
              {ginecoLleno.map(([k, l]) => (
                <tr key={k} className="border-b last:border-0">
                  <td className="py-1 pr-3 font-medium whitespace-nowrap align-top">{l}</td>
                  <td className="py-1 whitespace-pre-wrap">{gin[k]}</td>
                </tr>
              ))}
            </tbody></table>
          </div>
        )}
        {extras.filter(([k]) => txt[k]?.trim()).map(([k, l]) => (
          <div key={k}>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">{l}</h4>
            <p className="text-sm whitespace-pre-wrap">{txt[k]}</p>
          </div>
        ))}
        <div className="flex gap-2 border-t pt-3">
          <Button size="sm" variant="outline" onClick={imprimir}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
          <Button size="sm" variant="outline" onClick={() => setLectura(false)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
        </div>
      </Bloque>
    );
  }

  // ---- MODO EDICIÓN ----
  return (
    <Bloque titulo={titulo}>
      <Switch checked={sin} onChange={setSin} label="SIN PATOLOGÍA APARENTE" />
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 ${sin ? 'opacity-40 pointer-events-none' : ''}`}>
        {lista.map(([k, l]) => (
          <div key={k}>
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
              <input type="checkbox" className="accent-primary" checked={k in pat} onChange={() => togglePat(k)} disabled={sin} />
              {l}
            </label>
            {k in pat && (
              <AutoTextarea className="mt-1 text-xs" placeholder={k === 'discapacidad' ? 'Descripción' : 'Asignar detalle'}
                value={pat[k]} onChange={(e) => setPat((p) => ({ ...p, [k]: e.target.value }))} />
            )}
            {k === 'discapacidad' && k in pat && (
              <input className={`${fieldCls} mt-1 text-xs`} placeholder="Porcentaje"
                value={porcDisc} onChange={(e) => setPorcDisc(e.target.value)} />
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        {esPersonal && (
          <div className="w-full space-y-1">
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setHabOpen((o) => !o)}
                className={`rounded-md border px-2.5 py-1 text-xs hover:bg-muted ${Object.keys(hab).length ? 'border-primary/50 text-primary font-medium' : 'text-muted-foreground'}`}>
                Hábitos{Object.keys(hab).length ? ' •' : ''}
              </button>
              <button type="button" onClick={() => setGinOpen((o) => !o)}
                className={`rounded-md border px-2.5 py-1 text-xs hover:bg-muted ${Object.values(gin).some((v) => v?.trim()) ? 'border-primary/50 text-primary font-medium' : 'text-muted-foreground'}`}>
                Antecedentes Gineco-Obstétricos{Object.values(gin).some((v) => v?.trim()) ? ' •' : ''}
              </button>
            </div>
            {habOpen && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-2 rounded-md border p-3">
                {HABITOS.map(([k, l]) => (
                  <div key={k}>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" className="accent-primary" checked={k in hab} onChange={() => toggleHab(k)} /> {l}
                    </label>
                    {k in hab && <AutoTextarea className="mt-1 text-xs" placeholder="Detalle" value={hab[k]} onChange={(e) => setHab((p) => ({ ...p, [k]: e.target.value }))} />}
                  </div>
                ))}
              </div>
            )}
            {ginOpen && (
              <div ref={refGineco} className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border p-3">
                {GINECO.map(([k, l]) => (
                  <Campo key={k} label={l}>
                    <AutoTextarea value={gin[k] ?? ''} onChange={(e) => setGin((p) => ({ ...p, [k]: e.target.value }))} />
                  </Campo>
                ))}
              </div>
            )}
          </div>
        )}
        {extras.map(([k, l]) => (
          <Expandible key={k} label={l} value={txt[k] ?? ''} onChange={(v) => setTxt((t) => ({ ...t, [k]: v }))} />
        ))}
      </div>

      <div className="flex gap-2 border-t pt-3">
        <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
        {inicial && <Button size="sm" variant="outline" onClick={() => setLectura(true)}>Cancelar</Button>}
      </div>
    </Bloque>
  );
}

export function AntecedentesGenerales({ pacienteId, nombre, onVolver, focoGineco }: {
  pacienteId: string; nombre: string; onVolver: () => void; focoGineco?: boolean;
}) {
  const { data, isLoading } = useQuery<AntecedentesData>({
    queryKey: ['med-ficha', pacienteId, 'antecedentes'],
    queryFn: () => medRpc<AntecedentesData>('med_antecedentes', { _paciente_id: pacienteId }),
  });
  if (isLoading || !data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-4">
      <Cabecera titulo={focoGineco ? 'Antecedentes Gineco-Obstétricos' : 'Antecedentes generales'} onVolver={onVolver} />
      {!focoGineco && (
        <BloqueGeneral key={`f-${data.familiar?.updated_at ?? 'new'}`} pacienteId={pacienteId} nombre={nombre}
          tipo="familiar" titulo="Antecedentes Familiares" inicial={data.familiar} extras={EXTRAS_FAMILIAR} />
      )}
      <BloqueGeneral key={`p-${data.personal?.updated_at ?? 'new'}-${focoGineco ? 'g' : ''}`} pacienteId={pacienteId} nombre={nombre}
        tipo="personal" titulo="Antecedentes Personales" inicial={data.personal} extras={EXTRAS_PERSONAL} focoGineco={focoGineco} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pediátricos: prenatales / perinatales / postnatales (lectura ⇄ edición)
// ---------------------------------------------------------------------------
type Sec = Record<string, string | boolean>;
const s = (sec: Sec, k: string) => (typeof sec[k] === 'string' ? (sec[k] as string) : '');
const b = (sec: Sec, k: string) => sec[k] === true;

// Validaciones de recién nacido (reliv)
const validarPerinatal = (peri: Sec): string | null => {
  const peso = Number(s(peri, 'peso'));
  const talla = Number(s(peri, 'talla'));
  if (s(peri, 'peso') && peso > 5) return 'El valor del peso no puede ser mayor de 5 KG';
  if (s(peri, 'talla') && talla > 0.6) return 'El valor de la talla no puede ser mayor de 0.60 metros';
  return null;
};

function FormPediatrico({ pacienteId, nombre, inicial, onVolver }: {
  pacienteId: string; nombre: string; inicial: AntecedenteRow | null; onVolver: () => void;
}) {
  const qc = useQueryClient();
  const d = (inicial?.datos ?? {}) as Record<string, unknown>;
  const [lectura, setLectura] = useState(() => !!inicial);
  const [pre, setPre] = useState<Sec>(() => ({ ...((d.prenatales as Sec) ?? {}) }));
  const [peri, setPeri] = useState<Sec>(() => ({ ...((d.perinatales as Sec) ?? {}) }));
  const [post, setPost] = useState<Sec>(() => ({ ...((d.postnatales as Sec) ?? {}) }));

  const guardar = useMutation({
    mutationFn: () => {
      const err = validarPerinatal(peri);
      if (err) return Promise.reject(new Error(err));
      return medRpc('med_antecedente_guardar', {
        _paciente_id: pacienteId, _tipo: 'pediatrico', _sin_patologia: false,
        _datos: { prenatales: pre, perinatales: peri, postnatales: post },
      });
    },
    onSuccess: () => {
      toast.success('Antecedentes pediátricos guardados'); setLectura(true);
      void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const PRE_L: [string, string][] = [
    ['controles', 'Controles'], ['ecos', 'Ecos'], ['embarazo', 'Embarazo'],
    ['padre_nombre', 'Padre'], ['padre_cedula', 'Cédula Padre'], ['padre_fnac', 'Fecha Nac. Padre'], ['padre_edad', 'Edad Padre'], ['padre_sangre', 'Tipo Sangre Padre'], ['padre_profesion', 'Profesión Padre'], ['padre_talla', 'Talla Padre (m)'],
    ['madre_nombre', 'Madre'], ['madre_cedula', 'Cédula Madre'], ['madre_fnac', 'Fecha Nac. Madre'], ['madre_edad', 'Edad Madre'], ['madre_sangre', 'Tipo Sangre Madre'], ['madre_profesion', 'Profesión Madre'], ['madre_talla', 'Talla Madre (m)'],
    ['talla_diana', 'Talla Diana'], ['complicaciones', 'Complicaciones'], ['comentarios', 'Comentarios'],
  ];
  const PERI_L: [string, string][] = [
    ['tipo_parto', 'Tipo de Parto'], ['vacuna_hb', 'Vacuna Hepatitis B'], ['peso', 'Peso (kg)'], ['talla', 'Talla (m)'],
    ['perimetro_cefalico', 'Perímetro Cefálico'], ['apgar1', 'APGAR 1'], ['apgar5', 'APGAR 5'], ['apgar10', 'APGAR 10'],
    ['edad_gestacional', 'Edad Gestacional'], ['fnac_corregida', 'Fecha Nac. Corregida'], ['complicaciones', 'Complicaciones'],
  ];
  const PERI_B: [string, string][] = [['rcp', 'RCP'], ['profilaxis_optica', 'Profilaxis Óptica'], ['vitamina_k', 'Vitamina K'], ['prematuro', 'Prematuro']];
  const POST_L: [string, string][] = [
    ['alimentacion', 'Alimentación'], ['valoracion_neurologica', 'Valoración Neurológica'],
    ['tamizaje_metabolico', 'Tamizaje Metabólico'], ['tamizaje_auditivo', 'Tamizaje Auditivo'], ['comentarios', 'Comentarios'],
  ];

  const imprimir = () => {
    const filas: [string, string][] = [];
    PRE_L.forEach(([k, l]) => { if (s(pre, k).trim()) filas.push([`Prenatales — ${l}`, s(pre, k)]); });
    PERI_L.forEach(([k, l]) => { if (s(peri, k).trim()) filas.push([`Perinatales — ${l}`, s(peri, k)]); });
    PERI_B.forEach(([k, l]) => { if (b(peri, k)) filas.push([`Perinatales — ${l}`, 'Sí']); });
    POST_L.forEach(([k, l]) => { if (s(post, k).trim()) filas.push([`Postnatales — ${l}`, s(post, k)]); });
    imprimirBloque('Antecedentes Pediátricos', nombre, filas);
  };

  const inp = (sec: Sec, set: (f: (x: Sec) => Sec) => void, k: string, type = 'text') => (
    <input type={type} className={fieldCls} value={s(sec, k)} onChange={(e) => set((x) => ({ ...x, [k]: e.target.value }))} />
  );
  const check = (sec: Sec, set: (f: (x: Sec) => Sec) => void, k: string, l: string) => (
    <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
      <input type="checkbox" className="accent-primary" checked={b(sec, k)} onChange={(e) => set((x) => ({ ...x, [k]: e.target.checked }))} /> {l}
    </label>
  );
  const sangre = (sec: Sec, set: (f: (x: Sec) => Sec) => void, k: string) => (
    <select className={fieldCls} value={s(sec, k)} onChange={(e) => set((x) => ({ ...x, [k]: e.target.value }))}>
      <option value="">Elige…</option>{TIPOS_SANGRE.map((t) => <option key={t} value={t}>{t}</option>)}
    </select>
  );
  const persona = (pfx: 'padre' | 'madre', label: string) => (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      <Campo label={label}>{inp(pre, setPre, `${pfx}_nombre`)}</Campo>
      <Campo label={`Cédula ${label}`}>{inp(pre, setPre, `${pfx}_cedula`)}</Campo>
      <Campo label="Fecha de Nacimiento">{inp(pre, setPre, `${pfx}_fnac`, 'date')}</Campo>
      <Campo label={`Edad ${label}`}>{inp(pre, setPre, `${pfx}_edad`)}</Campo>
      <Campo label="Tipo Sangre">{sangre(pre, setPre, `${pfx}_sangre`)}</Campo>
      <Campo label="Profesión">{inp(pre, setPre, `${pfx}_profesion`)}</Campo>
    </div>
  );

  // ---- MODO LECTURA ----
  if (lectura) {
    const filas: [string, [string, string][]][] = [
      ['Antecedentes Prenatales', PRE_L.filter(([k]) => s(pre, k).trim()).map(([k, l]) => [l, s(pre, k)] as [string, string])],
      ['Antecedentes Perinatales', [
        ...PERI_L.filter(([k]) => s(peri, k).trim()).map(([k, l]) => [l, s(peri, k)] as [string, string]),
        ...PERI_B.filter(([k]) => b(peri, k)).map(([, l]) => [l, 'Sí'] as [string, string]),
      ]],
      ['Antecedentes Postnatales', POST_L.filter(([k]) => s(post, k).trim()).map(([k, l]) => [l, s(post, k)] as [string, string])],
    ];
    const vacio = filas.every(([, f]) => f.length === 0);
    return (
      <div className="space-y-4">
        <Cabecera titulo="Antecedentes pediátricos" onVolver={onVolver} />
        <Bloque titulo="Pediatría">
          {vacio && <p className="text-sm text-primary/80">No existen antecedentes pediátricos registrados</p>}
          {filas.filter(([, f]) => f.length > 0).map(([tit, f]) => (
            <div key={tit}>
              <h4 className="text-sm font-semibold">{tit}</h4>
              <table className="w-full text-sm"><tbody>
                {f.map(([l, v]) => (
                  <tr key={l} className="border-b last:border-0">
                    <td className="py-1 pr-3 font-medium whitespace-nowrap align-top w-56">{l}</td>
                    <td className="py-1 whitespace-pre-wrap">{v}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          ))}
          <div className="flex gap-2 border-t pt-3">
            <Button size="sm" variant="outline" onClick={imprimir}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
            <Button size="sm" variant="outline" onClick={() => setLectura(false)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
          </div>
        </Bloque>
      </div>
    );
  }

  // ---- MODO EDICIÓN ----
  return (
    <div className="space-y-4">
      <Cabecera titulo="Antecedentes pediátricos" onVolver={onVolver} />
      <Bloque titulo="Pediatría">
        {/* Prenatales */}
        <h4 className="text-sm font-semibold">Antecedentes Prenatales</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Campo label="Controles">{inp(pre, setPre, 'controles')}</Campo>
          <Campo label="Ecos">{inp(pre, setPre, 'ecos')}</Campo>
          <Campo label="Embarazo">{inp(pre, setPre, 'embarazo')}</Campo>
        </div>
        {persona('padre', 'Padre')}
        {persona('madre', 'Madre')}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Campo label="Talla Padre (m)">{inp(pre, setPre, 'padre_talla')}</Campo>
          <Campo label="Talla Madre (m)">{inp(pre, setPre, 'madre_talla')}</Campo>
          <Campo label="Talla Diana">{inp(pre, setPre, 'talla_diana')}</Campo>
        </div>
        <div className="flex flex-wrap gap-2">
          <Expandible label="Complicaciones" value={s(pre, 'complicaciones')} onChange={(v) => setPre((x) => ({ ...x, complicaciones: v }))} />
          <Expandible label="Comentarios" value={s(pre, 'comentarios')} onChange={(v) => setPre((x) => ({ ...x, comentarios: v }))} />
        </div>

        {/* Perinatales */}
        <h4 className="text-sm font-semibold border-t pt-3">Antecedentes Perinatales</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <Campo label="Tipo de Parto">{inp(peri, setPeri, 'tipo_parto')}</Campo>
          <Campo label="Vacuna Hepatitis B">{inp(peri, setPeri, 'vacuna_hb')}</Campo>
          {check(peri, setPeri, 'rcp', 'RCP')}
          {check(peri, setPeri, 'profilaxis_optica', 'Profilaxis Óptica')}
          {check(peri, setPeri, 'vitamina_k', 'Vitamina K')}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Campo label="Peso (kg)">{inp(peri, setPeri, 'peso')}</Campo>
          <Campo label="Talla (m)">{inp(peri, setPeri, 'talla')}</Campo>
          <Campo label="Perímetro Cefálico">{inp(peri, setPeri, 'perimetro_cefalico')}</Campo>
          <Campo label="APGAR 1 - 5 - 10">
            <div className="flex gap-1">
              {(['apgar1', 'apgar5', 'apgar10'] as const).map((k) => (
                <input key={k} className={`${fieldCls} w-14 text-center`} placeholder={k.replace('apgar', '')}
                  value={s(peri, k)} onChange={(e) => setPeri((x) => ({ ...x, [k]: e.target.value }))} />
              ))}
            </div>
          </Campo>
          <Campo label="Edad Gestacional">{inp(peri, setPeri, 'edad_gestacional')}</Campo>
        </div>
        {validarPerinatal(peri) && <p className="text-xs text-red-500">{validarPerinatal(peri)}</p>}
        <div className="flex flex-wrap items-end gap-4">
          {check(peri, setPeri, 'prematuro', 'Prematuro')}
          <Campo label="Fecha Nac. Corregida">{inp(peri, setPeri, 'fnac_corregida', 'date')}</Campo>
          <Expandible label="Complicaciones" value={s(peri, 'complicaciones')} onChange={(v) => setPeri((x) => ({ ...x, complicaciones: v }))} />
        </div>

        {/* Postnatales */}
        <h4 className="text-sm font-semibold border-t pt-3">Antecedentes Postnatales</h4>
        <div className="flex flex-wrap gap-2">
          {POST_L.map(([k, l]) => (
            <Expandible key={k} label={l} value={s(post, k)} onChange={(v) => setPost((x) => ({ ...x, [k]: v }))} />
          ))}
        </div>

        <div className="flex gap-2 border-t pt-3">
          <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar Antecedentes</Button>
          {inicial && <Button size="sm" variant="outline" onClick={() => setLectura(true)}>Cancelar</Button>}
        </div>
      </Bloque>
    </div>
  );
}

export function AntecedentesPediatricos({ pacienteId, nombre, onVolver }: { pacienteId: string; nombre: string; onVolver: () => void }) {
  const { data, isLoading } = useQuery<AntecedentesData>({
    queryKey: ['med-ficha', pacienteId, 'antecedentes'],
    queryFn: () => medRpc<AntecedentesData>('med_antecedentes', { _paciente_id: pacienteId }),
  });
  if (isLoading || !data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return <FormPediatrico key={data.pediatrico?.updated_at ?? 'new'} pacienteId={pacienteId} nombre={nombre} inicial={data.pediatrico} onVolver={onVolver} />;
}
